import type { IncidentView } from "./incident-model.js";
import { isWorsening } from "./incident-presentation.js";

export interface GroupItem {
  view: IncidentView;
  distanceKm: number;
  zoneName?: string;
}

export interface AlertGroup {
  key: string;
  hazard: string;
  event?: string;
  capSeverity?: string;
  members: GroupItem[]; // distance ascending
  nearestKm: number;
  worsening: boolean;
}

export type GroupedRow =
  | { kind: "single"; item: GroupItem }
  | { kind: "group"; group: AlertGroup };

/** Group key for fragment collapsing. Null (ungroupable) unless the incident
 *  carries a CAP severity — the authority/CAP signal. Crowd incidents (no
 *  capSeverity) are always ungrouped. */
export function groupKeyOf(view: IncidentView): string | null {
  const sev = view.capSeverity?.trim();
  if (!sev) return null;
  const token = (view.event ?? view.hazard).trim().toLowerCase();
  return `${view.hazard}|${token}|${sev}`;
}

/** Collapse fragments of one CAP warning. Null-key items and singleton keys
 *  become `single` rows (today's behavior); keys shared by >= 2 items become a
 *  `group`. Rows ordered worsening-first then nearest-distance. */
export function groupIncidents(items: GroupItem[]): GroupedRow[] {
  const buckets = new Map<string, GroupItem[]>();
  const rows: GroupedRow[] = [];
  for (const it of items) {
    const key = groupKeyOf(it.view);
    if (key === null) { rows.push({ kind: "single", item: it }); continue; }
    const arr = buckets.get(key);
    if (arr) arr.push(it); else buckets.set(key, [it]);
  }
  for (const [key, members] of buckets) {
    if (members.length === 1) { rows.push({ kind: "single", item: members[0] }); continue; }
    const sorted = [...members].sort((a, b) => a.distanceKm - b.distanceKm);
    const rep = sorted[0].view;
    rows.push({
      kind: "group",
      group: {
        key,
        hazard: rep.hazard,
        event: rep.event,
        capSeverity: rep.capSeverity,
        members: sorted,
        nearestKm: sorted[0].distanceKm,
        worsening: sorted.some((m) => isWorsening(m.view)),
      },
    });
  }
  const distOf = (r: GroupedRow) => (r.kind === "single" ? r.item.distanceKm : r.group.nearestKm);
  const worsOf = (r: GroupedRow) => (r.kind === "single" ? isWorsening(r.item.view) : r.group.worsening);
  return rows.sort((a, b) => Number(worsOf(b)) - Number(worsOf(a)) || distOf(a) - distOf(b));
}

import type { AlertEvent, Severity } from "./alert-model.js";

const RANK: Record<Severity, number> = { info: 1, warning: 2, critical: 3 };

/** Placeholder view for a synthesized group all-clear (the view is unused by all-clear
 *  rendering — the toast keys off hazard + the "tap" body). */
const STUB_VIEW: IncidentView = {
  subject: "", hazard: "other", status: "closed", W: 0, confidence: 0,
  cells: [], distinctIdentities: 0, cell: "", createdAtIso: "",
};

/** Collapses the per-subject AlertEngine stream into per-warning notifications.
 *  Stateful across evaluate() passes: tracks each group's active subject set so
 *  a group clears only when its LAST fragment clears, and so a burst of N "new"
 *  fragments yields a single toast. The AlertEngine itself is unchanged. */
export class GroupCoalescer {
  #active = new Map<string, Set<string>>();    // groupKey -> active subjects
  #toastedRank = new Map<string, number>();    // groupKey -> last-toasted severity rank
  #subjectGroup = new Map<string, string>();   // subject -> current group key. The AlertEngine
                                               // emits all-clears with a stripped stub view (no
                                               // capSeverity), so groupKeyOf can't key them — we
                                               // route an all-clear to the group its subject was
                                               // last active in instead.

  coalesce(events: AlertEvent[]): AlertEvent[] {
    interface Agg {
      hazard: string;            // group hazard (from the key) for the all-clear toast
      rep?: AlertEvent;          // representative active event
      repRank: number;
      wasEmpty: boolean;
      sawActive: boolean;
      inPath: boolean;
      cleared: boolean;
    }
    const passthrough: AlertEvent[] = [];
    const passes = new Map<string, Agg>();

    const setFor = (key: string): Set<string> => {
      let s = this.#active.get(key);
      if (!s) { s = new Set(); this.#active.set(key, s); }
      return s;
    };
    const aggFor = (key: string): Agg => {
      let a = passes.get(key);
      if (!a) {
        a = { hazard: key.split("|")[0], repRank: 0, wasEmpty: (this.#active.get(key)?.size ?? 0) === 0, sawActive: false, inPath: false, cleared: false };
        passes.set(key, a);
      }
      return a;
    };

    for (const e of events) {
      // Active events carry the real incident view → key by capSeverity. All-clear events
      // carry the engine's stub view (no capSeverity, hazard "other"), so route them to the
      // group the subject was last active in — never by their own view.
      const key = e.kind === "allClear"
        ? (this.#subjectGroup.get(e.subject) ?? null)
        : groupKeyOf(e.view);
      if (key === null) { passthrough.push(e); continue; }

      const a = aggFor(key);
      const set = setFor(key);

      if (e.kind === "allClear") {
        set.delete(e.subject);
        this.#subjectGroup.delete(e.subject);
        a.cleared = true;
        continue;
      }

      // A severity change re-keys the subject (capSeverity is part of the key). Drop it from
      // its prior band so that band can still drain (the empty band is swept below).
      const prevKey = this.#subjectGroup.get(e.subject);
      if (prevKey && prevKey !== key) this.#active.get(prevKey)?.delete(e.subject);

      set.add(e.subject);
      this.#subjectGroup.set(e.subject, key);
      a.sawActive = true;
      if (e.kind === "inPath") a.inPath = true;
      const rank = RANK[e.severity];
      if (!a.rep || rank > a.repRank || (rank === a.repRank && e.distanceKm < a.rep.distanceKm)) {
        a.rep = e;
        a.repRank = rank;
      }
    }

    const out: AlertEvent[] = [];
    for (const [key, a] of passes) {
      const set = this.#active.get(key) ?? new Set<string>();
      if (a.sawActive && a.rep) {
        const prevRank = this.#toastedRank.get(key) ?? 0;
        const escalated = a.repRank > prevRank;
        if (a.wasEmpty || escalated || a.inPath) {
          out.push({ ...a.rep, kind: a.inPath ? "inPath" : a.rep.kind, groupCount: set.size });
          this.#toastedRank.set(key, a.repRank);
        }
      }
      if (a.cleared && set.size === 0) {
        out.push({
          subject: `group:${key}`,
          hazard: a.hazard,
          severity: "info",
          kind: "allClear",
          distanceKm: 0,
          inPath: false,
          view: a.rep?.view ?? STUB_VIEW,
        });
      }
    }

    // Sweep groups emptied this pass (fully cleared, or vacated by a severity re-key) so
    // #active/#toastedRank can't grow without bound or leave a stale non-empty band that
    // would suppress a later re-fire (its wasEmpty would read false).
    for (const [k, s] of this.#active) {
      if (s.size === 0) { this.#active.delete(k); this.#toastedRank.delete(k); }
    }

    return [...out, ...passthrough];
  }
}
